<script lang="ts">
import type { ToastProviderProps } from 'reka-ui'

export interface ToasterProps extends Omit<ToastProviderProps, 'swipeDirection'> {
  portal?: boolean | string | HTMLElement
  class?: string | object
}

export interface ToasterSlots {
  default: (props?: object) => any
}

const ui = {
  viewport: 'fixed flex flex-col w-[calc(100%-2rem)] sm:w-96 z-[100] right-4 top-4 gap-4',
  base: 'pointer-events-auto absolute inset-x-0 transition-[transform,translate,height] duration-200 ease-out bottom-0',
}
</script>

<script setup lang="ts">
import { ToastPortal, ToastProvider, ToastViewport } from 'reka-ui'
import { toRef } from 'vue'
import { usePortal } from '../composables/usePortal'
import { useToast } from '../composables/useToast'
import { omit } from '../utils'

const props = withDefaults(defineProps<ToasterProps>(), {
  portal: true,
  duration: 5000,
})
defineSlots<ToasterSlots>()

const { toasts, remove } = useToast()

const portalProps = usePortal(toRef(() => props.portal))

function onUpdateOpen(value: boolean, id: string | number) {
  if (value) {
    return
  }
  remove(id)
}
</script>

<template>
  <ToastProvider swipe-direction="right">
    <slot />
    <AwToast
      v-for="(toast) of toasts"
      :key="toast.id"
      v-bind="omit(toast, ['id', 'isClosable'])"
      :is-closable="toast.isClosable"
      :data-expanded="false"
      :data-front="false"
      :class="[ui.base, toast.onClick && 'cursor-pointer']"
      @update:open="onUpdateOpen($event, toast.id)"
      @click="toast.onClick && toast.onClick(toast)"
    />
    <ToastPortal v-bind="portalProps">
      <ToastViewport
        :data-expanded="false"
        :class="[ui.viewport, props.class]"
      />
    </ToastPortal>
  </ToastProvider>
</template>
